import type {
  BreakSemantics,
  BreakTreatmentKind,
  BreakTreatmentSnapshotV1,
} from './break-treatment';

export const ROSTER_PRESUMED_BREAK_WARNING = 'pricing_break_presumed_from_roster';
export const ROSTER_PRESUMED_BREAK_POLICY = 'ROSTER_PRESUMED_V1';
export const BREAK_CONTRADICTIONS = [
  'recorded_minutes',
  'approved_correction',
  'approved_meal_break_fact',
  'runtime_break_metadata',
  'source_break_intervals',
  'assignment_break_row',
  'break_command',
  'capture_mismatch',
  'malformed_evidence',
] as const;
export const BREAK_OBSERVATIONS = [
  'runtime',
  'break_rows',
  'break_commands',
  'capture_mismatches',
  'approved_corrections',
  'approved_meal_break_facts',
] as const;
export interface RosterBreakIdentity {
  timesheetId: string;
  tenantId: string;
  staffId: string;
  assignmentId: string | null;
  shiftId: string | null;
}
/** A separate server-loaded value. Never obtained from source_evidence. */
export interface RosterBreakContext {
  identity: RosterBreakIdentity;
  evidence: unknown;
}
export function rosterScheduledBreakMinutes(context: RosterBreakContext): number | null {
  const value = breakEvidenceObject(context.evidence)?.scheduled_unpaid_break_minutes;
  return typeof value === 'number' && Number.isFinite(value) ? value : null;
}
export function breakEvidenceObject(value: unknown): Record<string, unknown> | null {
  return value !== null && typeof value === 'object' && !Array.isArray(value)
    ? (value as Record<string, unknown>)
    : null;
}
const upper = (v: unknown) => (typeof v === 'string' ? v.trim().toUpperCase() : null);
const positiveMinutes = (v: unknown): v is number =>
  typeof v === 'number' && Number.isSafeInteger(v) && v > 0;
export function originalBreakIsPresumed(value: unknown): boolean {
  const o = breakEvidenceObject(value);
  if (!o) return false;
  return (
    upper(o.breakEntryProvenance) === 'RULE_DEFAULT' ||
    [
      'SCHEDULED_ROSTER_PRESUMPTION',
      'ROSTER_CONFIRMED',
      'COORDINATOR_ON_BEHALF_CLOCK_OUT',
    ].includes(upper(o.captureMethod) ?? '') ||
    [o.break_source, o.break_minutes_authority, o.breakMinutesAuthority].some(
      (v) => upper(v) === 'ROSTERED_DEFAULT',
    )
  );
}
export function originalBreakHasRecordedMinutes(value: unknown): boolean {
  const o = breakEvidenceObject(value);
  return (
    !!o &&
    (['ENTERED', 'CONFIRMED_EXPLICIT_ZERO'].includes(upper(o.breakEntryProvenance) ?? '') ||
      [o.break_source, o.break_minutes_authority, o.breakMinutesAuthority].some((v) =>
        ['WORKER', 'APPROVED_TIME_BREAK_CORRECTION', 'DECLARED_AGGREGATE', 'RECORDED'].includes(
          upper(v) ?? '',
        ),
      ))
  );
}
export function originalBreakIsMalformed(value: unknown): boolean {
  const o = breakEvidenceObject(value);
  if (!o) return true;
  const authority = [
    'ROSTERED_DEFAULT',
    'UNRESOLVED',
    'WORKER',
    'APPROVED_TIME_BREAK_CORRECTION',
    'DECLARED_AGGREGATE',
    'RECORDED',
  ];
  const enums: Record<string, string[]> = {
    breakEntryProvenance: ['RULE_DEFAULT', 'ENTERED', 'CONFIRMED_EXPLICIT_ZERO'],
    // SQL6 cf2f5b0f98: RULE_DEFAULT permits every known capture method.
    captureMethod: [
      'SCHEDULED_ROSTER_PRESUMPTION',
      'ROSTER_CONFIRMED',
      'COORDINATOR_ON_BEHALF_CLOCK_OUT',
      'COORDINATOR_ON_BEHALF',
      'COORDINATOR_ON_BEHALF_CORRECTION',
      'AUTO_CLOSED_MISSED_CLOCK_OUT',
      'APP_CLOCK',
      'MANUAL',
      'WORKER_APP_CLOCK',
      'SUPERVISOR_CONFIRMED_AS_ROSTERED',
      'PAPER_SIGN_OFF_IMPORT',
      'WORKER_CONFIRMED',
      'PROVIDER_SYSTEM_IMPORT',
      'MIGRATION_SEED',
      'LEGACY_OPERATOR_ENTRY',
      'VISIT_LEVEL_CAPTURE',
      'INERVA_PAY_OUTPUT_VALIDATION_SEED',
      'INERVA_ROSTER_VALIDATION_SEED',
      'LOCKED_INERVA_DETAILED_PAY_LIST',
      'IMPORTED_LIVE_ROSTER_PROVIDER_DELEGATED_SIMULATION',
      'PROVIDER_PAYROLL_ROSTER_IMPORT',
      'PAPER SIGN-OFF',
      'ROSTER UPDATE',
      'MANAGER REVIEW',
      'COORDINATOR',
      'SHIFT ALLOCATOR',
      'EMAIL CONFIRMATION',
      'PROVIDER SOURCE WORKBOOK',
      'PHONE CONFIRMATION',
      'VISIT OUTCOME CORRECTION (COORDINATOR)',
      'AUTO_CLOSED',
      'AUTO_CLOSED_NO_CLOCK',
      'AUTO_CLOSED_MISSED_CLOCK_IN',
    ],
    break_source: authority,
    break_minutes_authority: authority,
    breakMinutesAuthority: authority,
  };
  return Object.entries(enums).some(
    ([k, allowed]) => o[k] != null && !allowed.includes(upper(o[k]) ?? ''),
  );
}

/** Closed, fail-closed B-MVP decision, shared by captured and frozen pay paths. */
export function classifyRosterPresumedBreak(
  input: RosterBreakContext & {
    breakSemantics?: BreakSemantics;
    treatmentKind: BreakTreatmentKind;
    /** Planning cannot attest absence of captured clock events. */
    evidenceKind?: 'CAPTURED_TIMESHEET' | 'PLANNING';
  },
): { kind: 'ROSTER_PRESUMED'; minutes: number } | { kind: 'NOT_ELIGIBLE'; reason: string } {
  const no = (reason: string) => ({ kind: 'NOT_ELIGIBLE' as const, reason });
  // Deliberately before reading evidence: OFF must not consult the classifier inputs.
  if (input.breakSemantics !== 'V13') return no('LEGACY_28D1');
  if (input.evidenceKind === 'PLANNING') return no('PLANNING');
  const e = breakEvidenceObject(input.evidence),
    id = input.identity;
  if (!e || e.policyVersion !== ROSTER_PRESUMED_BREAK_POLICY || e.evidence_state !== 'COMPLETE')
    return no('EVIDENCE_UNAVAILABLE');
  if (
    !id.timesheetId ||
    !id.tenantId ||
    !id.staffId ||
    !id.assignmentId ||
    e.timesheet_id !== id.timesheetId ||
    e.tenant_id !== id.tenantId ||
    e.staff_id !== id.staffId ||
    e.assignment_id !== id.assignmentId ||
    typeof e.shift_id !== 'string' ||
    !e.shift_id ||
    (id.shiftId !== null && e.shift_id !== id.shiftId)
  )
    return no('IDENTITY_MISMATCH');
  const o = breakEvidenceObject(e.original),
    flags = breakEvidenceObject(e.contradictions);
  if (
    !o ||
    originalBreakIsMalformed(o) ||
    flags?.malformed_evidence !== false ||
    typeof flags.ineligible_capture_method !== 'boolean'
  )
    return no('EVIDENCE_UNAVAILABLE');
  if (flags.ineligible_capture_method) return no('INELIGIBLE_CAPTURE_METHOD');
  if (!originalBreakIsPresumed(o) || originalBreakHasRecordedMinutes(o)) return no('NOT_PRESUMED');
  if (o.shift_id != null && o.shift_id !== e.shift_id) return no('IDENTITY_MISMATCH');
  const matches = e.roster_matches;
  if (e.roster_match_count !== 1 || !Array.isArray(matches) || matches.length !== 1)
    return no('ROSTER_AMBIGUOUS');
  const match = breakEvidenceObject(matches[0]);
  if (
    !match ||
    match.assignment_id !== id.assignmentId ||
    match.shift_id !== e.shift_id ||
    !positiveMinutes(e.scheduled_unpaid_break_minutes) ||
    match.scheduled_unpaid_break_minutes !== e.scheduled_unpaid_break_minutes ||
    // Paul's 28 October ruling: zero/missing on this independently presumed,
    // non-recorded original means nothing recorded; consume the roster minutes.
    (o.break_minutes != null &&
      o.break_minutes !== 0 &&
      o.break_minutes !== e.scheduled_unpaid_break_minutes) ||
    e.roster_minutes_match !== true
  )
    return no('ROSTER_MINUTES_MISMATCH');
  const observations = breakEvidenceObject(e.observations);
  if (
    !flags ||
    BREAK_CONTRADICTIONS.some((k) => flags[k] !== false) ||
    typeof flags.runtime_break_default_equal_roster !== 'boolean' ||
    !observations ||
    BREAK_OBSERVATIONS.some((k) => !Array.isArray(observations[k]))
  )
    return no('CONTRADICTED');
  for (const key of BREAK_OBSERVATIONS) {
    const values = observations[key] as unknown[];
    // SQL6 retains qualifying clock-out rollups. Require both its flags and
    // independent checks of every exposed row, including frozen evidence.
    if (!['runtime', 'break_rows', 'break_commands'].includes(key) && values.length)
      return no('CONTRADICTED');
    if (
      key === 'runtime' &&
      values.some((value) => {
        const r = breakEvidenceObject(value);
        if (
          !r ||
          (r.assignment_id !== null && (typeof r.assignment_id !== 'string' || !r.assignment_id)) ||
          r.malformed !== false ||
          !Array.isArray(r.break_metadata_keys) ||
          r.interval_count !== 0
        )
          return true;
        const keys = r.break_metadata_keys;
        const aliasKeys = ['breakMinutes', 'actualBreakMinutes'];
        const metadataKeys = [
          'break_minutes',
          ...aliasKeys,
          'break_source',
          'break_minutes_authority',
          'breakMinutesAuthority',
        ];
        if (keys.length === 0 && metadataKeys.every((k) => r[k] == null)) return false;
        // SQL scopes target/sibling rows (including legacy null-assignment rows).
        // Independently retain source, authority, duration and no-interval checks
        // for frozen evidence too; the flag attests every break-bearing row.
        const defaultAuthorities = ['ROSTERED_DEFAULT'];
        return (
          flags.runtime_break_default_equal_roster !== true ||
          upper(r.break_source) !== 'ROSTERED_DEFAULT' ||
          keys.some((k) => ![...metadataKeys, 'break_intervals', 'breakIntervals'].includes(k)) ||
          ['break_minutes_authority', 'breakMinutesAuthority'].some(
            (k) => r[k] != null && !defaultAuthorities.includes(upper(r[k]) ?? ''),
          ) ||
          !positiveMinutes(r.break_minutes) ||
          r.break_minutes !== e.scheduled_unpaid_break_minutes ||
          aliasKeys.some(
            (k) => (keys.includes(k) || r[k] != null) && r[k] !== e.scheduled_unpaid_break_minutes,
          )
        );
      })
    )
      return no('CONTRADICTED');
    if (
      key === 'break_rows' &&
      values.some((value) => {
        const r = breakEvidenceObject(value);
        // As with runtime rows, SQL attests target/sibling membership; legacy
        // null assignments remain permitted. No sibling ID list is exposed.
        return (
          !r ||
          (r.assignment_id !== null && (typeof r.assignment_id !== 'string' || !r.assignment_id)) ||
          (r.shift_id !== null && r.shift_id !== e.shift_id) ||
          r.kind !== 'BREAK' ||
          r.source_writer !== 'complete_shift_controlled' ||
          r.source_kind !== 'CLOCK_OUT' ||
          r.evidence_completeness !== 'DURATION_ONLY' ||
          r.duration_minutes !== e.scheduled_unpaid_break_minutes ||
          r.interval_from !== null ||
          r.interval_to !== null ||
          r.superseded_by_evidence_id !== null ||
          r.is_paid !== null
        );
      })
    )
      return no('CONTRADICTED');
    if (
      key === 'break_commands' &&
      values.some((value) => {
        const r = breakEvidenceObject(value);
        return (
          !r ||
          r.command_kind !== 'CAPTURE' ||
          r.command_state !== 'COMPLETED' ||
          (r.outcome !== 'APPLIED' && r.outcome !== 'ALREADY_APPLIED') ||
          r.predecessor_evidence_id !== null
        );
      })
    )
      return no('CONTRADICTED');
  }
  // Paid/below-trigger treatments are exempt from an unpaid deduction only
  // after the same complete identity, eligibility and contradiction checks.
  if (input.treatmentKind !== 'UNPAID') return no('NOT_UNPAID');
  return { kind: 'ROSTER_PRESUMED', minutes: e.scheduled_unpaid_break_minutes };
}

/** Only a unique source-bound entry may be consumed; old captures stay V13. */
export function rosterBreakContextForRow(
  row: Record<string, unknown>,
  evidence: unknown,
): RosterBreakContext {
  const entries = Array.isArray(evidence)
    ? evidence.filter((e) => breakEvidenceObject(e)?.timesheet_id === row.id)
    : [];
  return {
    identity: {
      timesheetId: String(row.id ?? ''),
      tenantId: String(row.tenant_id ?? ''),
      staffId: String(row.staff_id ?? ''),
      assignmentId: typeof row.roster_assignment_id === 'string' ? row.roster_assignment_id : null,
      shiftId: typeof row.shift_id === 'string' ? row.shift_id : null,
    },
    evidence: entries.length === 1 ? entries[0] : null,
  };
}

/** Synchronise engine facts and hours after the shared treatment decision. */
export function applyRosterPresumedBreakFacts(
  facts: Map<string, unknown>,
  treatment: BreakTreatmentSnapshotV1,
  approvedCorrectionMinutes?: number | null,
): void {
  if (
    treatment.actualBreakMinutesProvenance !== 'ROSTER_PRESUMED' &&
    approvedCorrectionMinutes == null
  )
    return;
  facts.set('MEAL_BREAK_DURATION', treatment.actualBreakMinutes);
  facts.set('MEAL_BREAK_TAKEN', treatment.actualBreakMinutes > 0);
  const hours = (treatment.workedMinutes + treatment.ordinaryTimeWorkedBreakMinutes) / 60;
  facts.set('ROSTERED_HOURS', hours);
  facts.set('USUAL_ROSTER', hours);
}

/** Reuse classified ordinary/OT carry only when its paid span agrees with the
 * accepted presumption. Repricing stale predecessors belongs to a fresh build;
 * never rescale their overtime bands by guessing a distribution. */
export function assertRosterPresumedCarryHours(
  row: {
    source_evidence?: unknown;
    roster_break_context?: RosterBreakContext;
    actual_start?: unknown;
    actual_end?: unknown;
    break_treatment_snapshot?: unknown;
  },
  pricedHours: unknown,
): void {
  const correction = breakEvidenceObject(
    breakEvidenceObject(row.source_evidence)?.approvedTimesheetCorrection,
  );
  if (correction?.kind === 'TIME_BREAK' && typeof correction.id === 'string' && correction.id)
    return;
  const treatment = breakEvidenceObject(row.break_treatment_snapshot);
  if (!row.roster_break_context || treatment?.treatmentKind !== 'UNPAID') return;
  const d = classifyRosterPresumedBreak({
    ...row.roster_break_context,
    breakSemantics: 'V13',
    treatmentKind: 'UNPAID',
  });
  if (d.kind !== 'ROSTER_PRESUMED') return;
  const span =
    typeof row.actual_start === 'string' && typeof row.actual_end === 'string'
      ? Math.round((Date.parse(row.actual_end) - Date.parse(row.actual_start)) / 60000)
      : NaN;
  if (
    typeof pricedHours !== 'number' ||
    !Number.isFinite(pricedHours) ||
    !Number.isFinite(span) ||
    Math.abs(pricedHours - Math.max(0, span - d.minutes) / 60) > 0.0001
  )
    throw new Error('pricing_prior_break_reprice_required');
}
